// editor-core.js — logica PURA dell'editor Fatturazione (fatt-v1, CONTRATTO-MVP §UI).
// NIENTE React, NIENTE DOM, NIENTE side-effect: mapping campo→sezione, shape di
// default (riga/pagamento/fattura vuote), etichette COMPLETE in italiano delle
// tabelle codici FatturaPA (trascritte da docs/fatturapa/REFERENCE.md, a sua
// volta verbatim dall'XSD v1.2.3), validazioni SOLO FORMATO del cliente e
// helper (debounce, formattazione euro, liste di interi).
// Importabile da node --test senza JSX (i test li aggiunge l'integratore).

// ---------------------------------------------------------------------------
// Tabelle codici (REFERENCE §3) — etichette italiane complete
// ---------------------------------------------------------------------------

// TipoDocumento: TD07..TD09 solo semplificata, TD10..TD15 NON esistono.
export const TIPI_DOCUMENTO = [
  { code: 'TD01', label: 'Fattura' },
  { code: 'TD02', label: 'Acconto/anticipo su fattura' },
  { code: 'TD03', label: 'Acconto/anticipo su parcella' },
  { code: 'TD04', label: 'Nota di credito' },
  { code: 'TD05', label: 'Nota di debito' },
  { code: 'TD06', label: 'Parcella' },
  { code: 'TD16', label: 'Integrazione fattura reverse charge interno' },
  { code: 'TD17', label: 'Integrazione/autofattura acquisto servizi dall\'estero' },
  { code: 'TD18', label: 'Integrazione acquisto beni intracomunitari' },
  { code: 'TD19', label: 'Integrazione/autofattura acquisto beni ex art.17 c.2' },
  { code: 'TD20', label: 'Autofattura per regolarizzazione' },
  { code: 'TD21', label: 'Autofattura per splafonamento' },
  { code: 'TD22', label: 'Estrazione beni da Deposito IVA' },
  { code: 'TD23', label: 'Estrazione beni da Deposito IVA con versamento IVA' },
  { code: 'TD24', label: 'Fattura differita (art.21 c.4 lett. a)' },
  { code: 'TD25', label: 'Fattura differita (art.21 c.4 lett. b)' },
  { code: 'TD26', label: 'Cessione beni ammortizzabili / passaggi interni' },
  { code: 'TD27', label: 'Autofattura autoconsumo / cessioni gratuite' },
  { code: 'TD28', label: 'Acquisti da San Marino con IVA (cartacea)' },
  { code: 'TD29', label: 'Comunicazione omessa/irregolare fatturazione' },
];

// I 4 "in evidenza" del livello base (mandato UI) — TD02/TD03 restano nel
// primo optgroup "Documenti", il resto nel gruppo Integrazioni/Autofatture.
export const TD_EVIDENZA = ['TD01', 'TD04', 'TD05', 'TD06'];
export const TD_BASE = ['TD01', 'TD02', 'TD03', 'TD04', 'TD05', 'TD06'];
export const TD_INTEGRAZIONI = TIPI_DOCUMENTO.map((t) => t.code).filter((c) => TD_BASE.indexOf(c) === -1);

// TD "ruoli invertiti" (documento emesso dal cessionario: unicità/P.IVA in capo
// al cessionario, scarti 00473/00475). TD21/TD27 NON invertono (cedente =
// cessionario, 00472); TD24..TD26 sono fatture normali del cedente.
export const TD_RUOLI_INVERTITI = ['TD16', 'TD17', 'TD18', 'TD19', 'TD20', 'TD22', 'TD23', 'TD28', 'TD29'];

export function tdRuoliInvertiti(td) { return TD_RUOLI_INVERTITI.indexOf(td) !== -1; }
export function tdIntegrazione(td) { return TD_INTEGRAZIONI.indexOf(td) !== -1; }

export function tdLabel(code) {
  const t = TIPI_DOCUMENTO.filter((x) => x.code === code)[0];
  return t ? t.label : code;
}

// Natura — sottocodici COMPLETI (N2/N3/N6 generici vietati dal 2021, scarto
// 00445: NON compaiono nel select).
export const NATURE = [
  { code: 'N1', label: 'Escluse ex art. 15 DPR 633/72' },
  { code: 'N2.1', label: 'Non soggette — artt. 7 a 7-septies DPR 633/72' },
  { code: 'N2.2', label: 'Non soggette — altri casi (es. forfettario)' },
  { code: 'N3.1', label: 'Non imponibili — esportazioni' },
  { code: 'N3.2', label: 'Non imponibili — cessioni intracomunitarie' },
  { code: 'N3.3', label: 'Non imponibili — cessioni verso San Marino' },
  { code: 'N3.4', label: 'Non imponibili — assimilate alle esportazioni' },
  { code: 'N3.5', label: 'Non imponibili — dichiarazioni d\'intento' },
  { code: 'N3.6', label: 'Non imponibili — altre operazioni fuori plafond' },
  { code: 'N4', label: 'Esenti' },
  { code: 'N5', label: 'Regime del margine / IVA non esposta' },
  { code: 'N6.1', label: 'Inversione contabile — rottami e recupero' },
  { code: 'N6.2', label: 'Inversione contabile — oro e argento' },
  { code: 'N6.3', label: 'Inversione contabile — subappalto edile' },
  { code: 'N6.4', label: 'Inversione contabile — cessione fabbricati' },
  { code: 'N6.5', label: 'Inversione contabile — telefoni cellulari' },
  { code: 'N6.6', label: 'Inversione contabile — prodotti elettronici' },
  { code: 'N6.7', label: 'Inversione contabile — comparto edile e connessi' },
  { code: 'N6.8', label: 'Inversione contabile — settore energetico' },
  { code: 'N6.9', label: 'Inversione contabile — altri casi' },
  { code: 'N7', label: 'IVA assolta in altro Stato UE' },
];

// Preset del selettore IVA di riga: la stessa percentuale con nature diverse è
// un'operazione fiscalmente DIVERSA (reverse charge, esenzioni, export…) —
// il selettore combina aliquota+natura in un colpo solo. value = 'A' (aliquota
// piena) oppure '0|<natura>'. I gruppi tengono in testa i casi edili (N6.3/N6.7).
export const IVA_PRESETS = [
  { gruppo: 'Aliquote', voci: [
    { value: '22', label: '22%', aliquota: 22, natura: '' },
    { value: '10', label: '10%', aliquota: 10, natura: '' },
    { value: '5', label: '5%', aliquota: 5, natura: '' },
    { value: '4', label: '4%', aliquota: 4, natura: '' },
  ] },
  { gruppo: '0% — Inversione contabile (reverse charge)', voci: [
    'N6.3', 'N6.7', 'N6.1', 'N6.2', 'N6.4', 'N6.5', 'N6.6', 'N6.8', 'N6.9',
  ].map(nat0) },
  { gruppo: '0% — Non imponibili', voci: [
    'N3.1', 'N3.2', 'N3.3', 'N3.4', 'N3.5', 'N3.6',
  ].map(nat0) },
  { gruppo: '0% — Esenti / escluse / altro', voci: [
    'N4', 'N1', 'N2.1', 'N2.2', 'N5', 'N7',
  ].map(nat0) },
];
function nat0(code) {
  const n = NATURE.find((x) => x.code === code) || { code, label: code };
  return { value: '0|' + code, label: '0% — ' + code + ' ' + n.label, aliquota: 0, natura: code };
}
// (aliquotaIva, natura) → value del selettore; natura assente con IVA 0 → '0|' (placeholder).
export function ivaPresetValue(aliquotaIva, natura) {
  if (Number(aliquotaIva) !== 0) return String(aliquotaIva);
  return '0|' + (natura || '');
}
// value del selettore → { aliquotaIva, natura }.
export function ivaPresetParse(value) {
  const s = String(value == null ? '' : value);
  if (s.indexOf('0|') === 0) return { aliquotaIva: 0, natura: s.slice(2) };
  return { aliquotaIva: Number(s) || 0, natura: '' };
}

export const MODALITA_PAGAMENTO = [
  { code: 'MP01', label: 'Contanti' },
  { code: 'MP02', label: 'Assegno' },
  { code: 'MP03', label: 'Assegno circolare' },
  { code: 'MP04', label: 'Contanti presso Tesoreria' },
  { code: 'MP05', label: 'Bonifico' },
  { code: 'MP06', label: 'Vaglia cambiario' },
  { code: 'MP07', label: 'Bollettino bancario' },
  { code: 'MP08', label: 'Carta di pagamento' },
  { code: 'MP09', label: 'RID' },
  { code: 'MP10', label: 'RID utenze' },
  { code: 'MP11', label: 'RID veloce' },
  { code: 'MP12', label: 'RIBA' },
  { code: 'MP13', label: 'MAV' },
  { code: 'MP14', label: 'Quietanza erario' },
  { code: 'MP15', label: 'Giroconto su contabilità speciale' },
  { code: 'MP16', label: 'Domiciliazione bancaria' },
  { code: 'MP17', label: 'Domiciliazione postale' },
  { code: 'MP18', label: 'Bollettino c/c postale' },
  { code: 'MP19', label: 'SEPA Direct Debit' },
  { code: 'MP20', label: 'SEPA DD CORE' },
  { code: 'MP21', label: 'SEPA DD B2B' },
  { code: 'MP22', label: 'Trattenuta su somme già riscosse' },
  { code: 'MP23', label: 'PagoPA' },
];

export const CONDIZIONI_PAGAMENTO = [
  { code: 'TP01', label: 'Pagamento a rate' },
  { code: 'TP02', label: 'Pagamento completo' },
  { code: 'TP03', label: 'Anticipo' },
];

export const TIPI_RITENUTA = [
  { code: 'RT01', label: 'Ritenuta d\'acconto persone fisiche' },
  { code: 'RT02', label: 'Ritenuta persone giuridiche' },
  { code: 'RT03', label: 'Contributo INPS' },
  { code: 'RT04', label: 'Contributo ENASARCO' },
  { code: 'RT05', label: 'Contributo ENPAM' },
  { code: 'RT06', label: 'Altro contributo previdenziale' },
];

// CausalePagamento (codici CU) — 'Z' escluso: non più valido dal 1/1/2021.
export const CAUSALI_PAGAMENTO = [
  'A', 'B', 'C', 'D', 'E', 'G', 'H', 'I', 'L', 'L1', 'M', 'M1', 'M2', 'N',
  'O', 'O1', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'V1', 'W', 'X', 'Y', 'ZO',
];

export const TIPI_CASSA = [
  { code: 'TC01', label: 'Cassa avvocati' },
  { code: 'TC02', label: 'Cassa dottori commercialisti' },
  { code: 'TC03', label: 'Cassa geometri' },
  { code: 'TC04', label: 'Inarcassa (ingegneri e architetti)' },
  { code: 'TC05', label: 'Cassa del notariato' },
  { code: 'TC06', label: 'Cassa ragionieri e periti commerciali' },
  { code: 'TC07', label: 'ENASARCO' },
  { code: 'TC08', label: 'ENPACL (consulenti del lavoro)' },
  { code: 'TC09', label: 'ENPAM (medici)' },
  { code: 'TC10', label: 'ENPAF (farmacisti)' },
  { code: 'TC11', label: 'ENPAV (veterinari)' },
  { code: 'TC12', label: 'ENPAIA (agricoltura)' },
  { code: 'TC13', label: 'Fondo imprese di spedizione / agenzie marittime' },
  { code: 'TC14', label: 'INPGI (giornalisti)' },
  { code: 'TC15', label: 'ONAOSI' },
  { code: 'TC16', label: 'CASAGIT' },
  { code: 'TC17', label: 'EPPI (periti industriali)' },
  { code: 'TC18', label: 'EPAP (pluricategoriale)' },
  { code: 'TC19', label: 'ENPAB (biologi)' },
  { code: 'TC20', label: 'ENPAPI (infermieri)' },
  { code: 'TC21', label: 'ENPAP (psicologi)' },
  { code: 'TC22', label: 'INPS' },
];

export const TIPI_CESSIONE = [
  { code: '', label: '— nessuno —' },
  { code: 'SC', label: 'Sconto' },
  { code: 'PR', label: 'Premio' },
  { code: 'AB', label: 'Abbuono' },
  { code: 'AC', label: 'Spesa accessoria' },
];

export const ALIQUOTE_IVA = [22, 10, 5, 4, 0];

// Etichette stato (MVP: solo bozza; i colori del piano vietano il rosso per
// non_consegnata — qui comunque solo 'bozza' grigio).
export const STATO_LABEL = { bozza: 'Bozza' };

// ---------------------------------------------------------------------------
// Shape di default (CONTRATTO §SHAPE RECORD FATTURA)
// ---------------------------------------------------------------------------

export function buildRigaVuota() {
  return {
    id: null, position: 0, numeroLinea: 0,
    tipoCessione: '', codiciArticolo: [],
    descrizione: '', quantita: null, unitaMisura: '',
    dataInizioPeriodo: null, dataFinePeriodo: null,
    prezzoUnitario: 0, sconti: [], prezzoTotale: 0,
    aliquotaIva: 22, natura: '', ritenuta: false,
    riferimentoAmministrazione: '', altriDatiGestionali: [],
  };
}

export function buildRataVuota() {
  return {
    beneficiario: '', modalita: 'MP05', dataRiferimento: null, giorniTermini: null,
    dataScadenza: null, importo: null, codUfficioPostale: '',
    cognomeQuietanzante: '', nomeQuietanzante: '', cfQuietanzante: '', titoloQuietanzante: '',
    istituto: '', iban: '', abi: '', cab: '', bic: '',
    scontoAnticipato: null, dataLimiteAnticipato: null,
    penalita: null, dataDecorrenzaPenale: null, codicePagamento: '',
  };
}

export function buildPagamentoVuoto() {
  return { condizioni: 'TP02', rate: [buildRataVuota()] };
}

export function buildDatiGeneraliVuoti() {
  return {
    ritenute: [], bollo: null, casse: [], scontiDoc: [],
    importoTotale: null, arrotondamento: null, causali: [],
    datiOrdine: [], datiContratto: [], datiConvenzione: [], datiRicezione: [], fattureCollegate: [],
    datiSal: [], datiDdt: [], datiTrasporto: null, fatturaPrincipale: null, datiVeicoli: null,
  };
}

export function buildDocRifVuoto() {
  return { riferimentoLinee: [], idDocumento: '', data: null, numItem: '', commessa: '', cup: '', cig: '' };
}

// oggi in ISO locale (YYYY-MM-DD) — niente shift UTC di toISOString a cavallo
// di mezzanotte.
export function oggiISO(d) {
  const x = d instanceof Date ? d : new Date();
  const mm = String(x.getMonth() + 1).padStart(2, '0');
  const dd = String(x.getDate()).padStart(2, '0');
  return x.getFullYear() + '-' + mm + '-' + dd;
}

export function buildFatturaVuota(oggi) {
  const data = oggi || oggiISO();
  return {
    id: null, direction: 'out', stato: 'bozza',
    formato: 'FPR12', tipoDocumento: 'TD01',
    divisa: 'EUR', dataEmissione: data,
    serie: '', anno: parseInt(data.slice(0, 4), 10), numero: null, numeroCustom: null,
    art73: false, clienteId: null, ruoliInvertiti: false,
    cedente: null, cessionario: null,
    codiceDestinatario: '0000000', pecDestinatario: '',
    datiGenerali: buildDatiGeneraliVuoti(),
    righe: [buildRigaVuota()],
    pagamento: buildPagamentoVuoto(),
    riepiloghi: [], totaleImponibile: 0, totaleImposta: 0, totaleDocumento: 0,
    totaleRitenute: 0, nettoAPagare: 0, bolloProposto: false,
    version: 0, createdAt: '', updatedAt: '',
  };
}

// ---------------------------------------------------------------------------
// mappaCampoSezione(path) → id sezione dell'editor.
// path = campo puntato del validatore (es. 'righe[2].natura',
// 'cessionario.sede.cap', 'datiGenerali.ritenute'). Le sezioni BASE sono
// cliente/documento/righe/pagamento (sempre visibili); le altre sono le
// sezioni avanzate collassabili (id DOM: f1-sez-<id>).
// ---------------------------------------------------------------------------
export const SEZIONI_AVANZATE = [
  'ritenute', 'cassa', 'bollo', 'sconti', 'causali', 'altro',
  'doccollegati', 'ddt', 'trasporto', 'fattprincipale', 'veicoli',
];

export function mappaCampoSezione(path) {
  const p = String(path || '');
  if (p.indexOf('righe') === 0) return 'righe';
  if (p.indexOf('pagamento') === 0) return 'pagamento';
  if (p.indexOf('cessionario') === 0 || p === 'clienteId'
    || p.indexOf('codiceDestinatario') === 0 || p.indexOf('pecDestinatario') === 0) return 'cliente';
  if (p.indexOf('datiGenerali.ritenute') === 0) return 'ritenute';
  if (p.indexOf('datiGenerali.casse') === 0) return 'cassa';
  if (p.indexOf('datiGenerali.bollo') === 0) return 'bollo';
  if (p.indexOf('datiGenerali.scontiDoc') === 0
    || p.indexOf('datiGenerali.importoTotale') === 0
    || p.indexOf('datiGenerali.arrotondamento') === 0) return 'sconti';
  if (p.indexOf('datiGenerali.causali') === 0) return 'causali';
  if (/^datiGenerali\.(datiOrdine|datiContratto|datiConvenzione|datiRicezione|fattureCollegate|datiSal)/.test(p)) return 'doccollegati';
  if (p.indexOf('datiGenerali.datiDdt') === 0) return 'ddt';
  if (p.indexOf('datiGenerali.datiTrasporto') === 0) return 'trasporto';
  if (p.indexOf('datiGenerali.fatturaPrincipale') === 0) return 'fattprincipale';
  if (p.indexOf('datiGenerali.datiVeicoli') === 0) return 'veicoli';
  if (p === 'art73' || p === 'divisa') return 'altro';
  if (p.indexOf('riepiloghi') === 0 || p.indexOf('totale') === 0 || p === 'nettoAPagare' || p === 'bolloProposto') return 'righe';
  return 'documento'; // tipoDocumento, dataEmissione, serie, numero, formato, cedente…
}

// badge 'compilato' delle sezioni avanzate (predicato puro sul record)
export function sezioneCompilata(f, sez) {
  if (!f) return false;
  const dg = f.datiGenerali || {};
  switch (sez) {
    case 'ritenute': return (dg.ritenute || []).length > 0;
    case 'cassa': return (dg.casse || []).length > 0;
    case 'bollo': return !!dg.bollo;
    case 'sconti': return (dg.scontiDoc || []).length > 0 || dg.importoTotale != null || dg.arrotondamento != null;
    case 'causali': return (dg.causali || []).some((c) => c && String(c).trim());
    case 'altro': return !!f.art73 || (!!f.divisa && f.divisa !== 'EUR');
    case 'doccollegati':
      return ['datiOrdine', 'datiContratto', 'datiConvenzione', 'datiRicezione', 'fattureCollegate']
        .some((k) => (dg[k] || []).length > 0);
    case 'ddt': return (dg.datiDdt || []).length > 0;
    case 'trasporto': return !!dg.datiTrasporto;
    case 'fattprincipale': return !!dg.fatturaPrincipale;
    case 'veicoli': return !!dg.datiVeicoli;
    default: return false;
  }
}

// avanzato per-riga compilato? (badge sul bottone drawer della riga)
export function rigaAvanzataCompilata(r) {
  if (!r) return false;
  return !!(r.tipoCessione || (r.codiciArticolo || []).length || (r.sconti || []).length
    || r.dataInizioPeriodo || r.dataFinePeriodo || (r.altriDatiGestionali || []).length
    || r.riferimentoAmministrazione || r.ritenuta);
}

// ---------------------------------------------------------------------------
// Validazioni SOLO FORMATO (cliente) — mai regole di business: campo presente
// ⇒ formato giusto; obbligatoria la sola scelta anagrafica.
// ---------------------------------------------------------------------------
export function validaClienteFormato(c) {
  const err = {};
  const den = (c.denominazione || '').trim();
  const nome = (c.nome || '').trim();
  const cogn = (c.cognome || '').trim();
  if (!den && !(nome && cogn)) {
    err.denominazione = 'Serve la denominazione oppure nome e cognome';
  }
  if (den && (nome || cogn)) {
    err.denominazione = 'Denominazione OPPURE nome+cognome, non entrambi';
  }
  const paese = (c.idPaese || 'IT').toUpperCase();
  if (c.partitaIva) {
    if (paese === 'IT' && !/^\d{11}$/.test(c.partitaIva)) err.partitaIva = 'P.IVA italiana: 11 cifre';
    else if (!/^[A-Za-z0-9]{1,28}$/.test(c.partitaIva)) err.partitaIva = 'Formato non valido (max 28 alfanumerici)';
  }
  if (c.codiceFiscale && !/^[A-Za-z0-9]{11,16}$/.test(c.codiceFiscale)) {
    err.codiceFiscale = 'Codice fiscale: 11-16 caratteri alfanumerici';
  }
  if (c.cap && (c.nazione || 'IT').toUpperCase() === 'IT' && !/^\d{5}$/.test(c.cap)) {
    err.cap = 'CAP italiano: 5 cifre';
  }
  if (c.provincia && !/^[A-Za-z]{2}$/.test(c.provincia)) {
    err.provincia = 'Provincia: 2 lettere (es. NA)';
  }
  if (c.codiceDestinatario && !/^[A-Za-z0-9]{6,7}$/.test(c.codiceDestinatario)) {
    err.codiceDestinatario = 'Codice SDI: 6-7 caratteri alfanumerici';
  }
  if (c.pec && !/^\S+@\S+\.\S+$/.test(c.pec)) {
    err.pec = 'PEC non valida';
  }
  if (c.idPaese && !/^[A-Za-z]{2}$/.test(c.idPaese)) {
    err.idPaese = 'Paese: 2 lettere ISO (es. IT)';
  }
  if (c.nazione && !/^[A-Za-z]{2}$/.test(c.nazione)) {
    err.nazione = 'Nazione: 2 lettere ISO (es. IT)';
  }
  if (c.codiceUfficioPa && !/^[A-Za-z0-9]{6}$/.test(c.codiceUfficioPa)) {
    err.codiceUfficioPa = 'Codice ufficio PA: 6 caratteri';
  }
  return err;
}

// IBAN — pattern XSD (solo formato, se presente)
export function ibanValido(iban) {
  return /^[a-zA-Z]{2}[0-9]{2}[a-zA-Z0-9]{11,30}$/.test(iban);
}

// ---------------------------------------------------------------------------
// Helper generici
// ---------------------------------------------------------------------------

// debounce con cancel/flush (autosave 2s + flush on-blur)
export function debounce(fn, ms) {
  let t = null;
  let lastArgs = null;
  function run() { t = null; const a = lastArgs; lastArgs = null; fn.apply(null, a || []); }
  function debounced() {
    lastArgs = Array.prototype.slice.call(arguments);
    if (t) clearTimeout(t);
    t = setTimeout(run, ms);
  }
  debounced.cancel = function () { if (t) clearTimeout(t); t = null; lastArgs = null; };
  debounced.flush = function () { if (t) { clearTimeout(t); run(); } };
  debounced.pending = function () { return t != null; };
  return debounced;
}

// '1,2,3' ⇄ [1,2,3] (RiferimentoNumeroLinea nei documenti collegati / DDT)
export function parseIntList(s) {
  return String(s || '').split(',').map((x) => parseInt(x.trim(), 10)).filter((n) => !isNaN(n) && n > 0);
}
export function joinIntList(a) {
  return (a || []).join(', ');
}

// numero da input: '' → null, altrimenti Number (virgola tollerata)
export function numOrNull(v) {
  if (v === '' || v == null) return null;
  const n = Number(String(v).replace(',', '.'));
  return isNaN(n) ? null : n;
}

// formattazione importi in stile italiano (solo display)
export function fmtEuro(n) {
  if (n == null || isNaN(Number(n))) return '—';
  const x = Number(n);
  const neg = x < 0;
  const s = Math.abs(x).toFixed(2);
  const parts = s.split('.');
  let int = parts[0];
  let out = '';
  while (int.length > 3) { out = '.' + int.slice(-3) + out; int = int.slice(0, -3); }
  out = int + out;
  return (neg ? '−' : '') + out + ',' + parts[1] + ' €';
}

export function fmtOra(d) {
  const x = d instanceof Date ? d : new Date(d);
  return String(x.getHours()).padStart(2, '0') + ':' + String(x.getMinutes()).padStart(2, '0');
}

export function fmtDay(iso) {
  if (!iso) return '—';
  const p = String(iso).slice(0, 10).split('-');
  return p[2] + '/' + p[1] + '/' + p[0];
}

// nome visualizzato di un cliente (DTO contratto)
export function nomeCliente(c) {
  if (!c) return '—';
  const den = (c.denominazione || '').trim();
  if (den) return den;
  const nc = ((c.nome || '') + ' ' + (c.cognome || '')).trim();
  return nc || '—';
}
